import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAllCitiesViewModel } from "./AllCityViewModel.js";
// import AddClient from "../AddClient/AddClient";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import CountriesIcon from "@mui/icons-material/Public"
import { useLoading } from "../../contexts/LoadingContext";
import Modal from "../../components/Modal/Modal";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import AddCity from "./AddCity.js";
import { useRbac } from "../../hooks/RbacContext.js";

const CityLanding = () => {
  const {
    cities,
    loading,
    error,
    handleDelete,
    createCity,
    updateCity,
    refetch,
    handleSearch,
    countries,
    getCountryName
  } = useAllCitiesViewModel();

  const { showLoading, hideLoading } = useLoading();

  const [isAddCityOpen, setIsAddCityOpen] = useState(false);
  const [selectedCity, setSelectedCity] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const{checkAccess} = useRbac();

  const columns = [
    {
      id: "city_name",
      label: "Name",
    },
    {
      id: "country_id",
      label: "Country",
      render: (value) => (
        <Typography
          variant="body2"
          noWrap
          sx={{
            fontSize: "inherit",
            color: (theme) =>
              value ? theme.palette.text.primary : theme.palette.text.secondary,
          }}
        >
          {getCountryName(value)}
        </Typography>
      ),
      searchValue: (value) => getCountryName(value),
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
    },

    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
    },
  ];
  const fixedColumns = {
    left: [],
    right: ["actions"],
  };
  const handleAddClient = () => {
    setSelectedCity(null);
    setIsAddCityOpen(true);
  };

  const handleEditClient = (client) => {
    setSelectedCity(client);
    setIsAddCityOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddCityOpen(false);
    setSelectedCity(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedCity) {
        await updateCity(selectedCity.city_id, formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "City updated successfully",
        });
      } else {
        await createCity(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "City added successfully",
        });
      }
      handleCloseDialog();
      await refetch();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (city) => {
    setDeleteConfirmation({
      city,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const city = deleteConfirmation.city;
    try {
      showLoading();
      await handleDelete(city);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "City deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete City",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (client) => {
    
    const actions = [];
    if (checkAccess("city", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditClient,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("city", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  if (error) {
    return (
      <div>
        <Typography color="error">Error: {error}</Typography>
      </div>
    );
  }
  const columnFilters = [
    {
      id: "country_id",
      label: "Country",
      render: (value) => (
          getCountryName(value)
      ),
      searchValue: (value) => getCountryName(value),
      icon: <CountriesIcon/>
    }
  ];

  return (
    <div>
      <CustomTable
        columns={columns}
        data={cities}
        onAdd={handleAddClient}
        showActions={true}
        showAddButton={checkAccess("city","add")}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="All Cities"
        subtitle="Manage and monitor all cities"
        addButtonText="Add City"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddCity
        open={isAddCityOpen}
        onClose={handleCloseDialog}
        initialData={selectedCity}
        onSubmit={handleSubmit}
        countries={countries}
        getCountryName={getCountryName}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete City"
          message={`Are you sure you want to delete city "${deleteConfirmation.city.city_name}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default CityLanding;
